<template>
  <bottom-sheet
    :visible="visible"
    :close-icon-right-align="true"
    close-icon-class="top-[22px]"
    :max-width="isMobile ? 'max-w-[90vw]' : 'max-w-[450px]'"
    @close="closeModal"
  >
    <div
      v-if="showStaticModal"
      class="flex flex-col gap-3 px-3 pb-2"
    >
      <h4 class="-mt-5 text-xl text-steel-1100">Offer Placed</h4>

      <div class="flex items-center justify-between">
        <img
          v-if="brokerLogo"
          class="flex h-8 max-w-48 items-center text-2xs"
          :src="brokerLogo"
          alt="broker logo"
        />

        <div class="flex flex-col items-end gap-2 pt-2">
          <h3 class="text-2xl font-semibold">
            {{ formatPrice(load.price) }}
          </h3>
          <p
            v-if="ratePerMile && load?.price"
            class="-mt-2 text-1xs text-neutral-subtlest"
          >
            {{ ratePerMile }}/mi
          </p>
        </div>
      </div>

      <p class="font-normal text-steel-900">
        You've successfully placed an offer of
        {{ formatPrice(placed_offer?.rate) }}
        for load #{{ load?.external_order_or_load_number }} with
        {{ load?.broker_name }}. This broker's system does not support in-app
        negotiation. You need to contact them immediately to continue
        negotiations.
      </p>

      <p class="font-normal text-steel-900">
        Phone:
        {{
          formatPhoneParenthesis(
            load?.broker_phone_number,
            load?.broker_phone_extension
          ) || '-'
        }}
      </p>

      <p class="font-normal text-steel-900">
        Email:
        <a
          :href="`mailto:${load.broker_email}`"
          class="text-sun-700 underline"
        >
          {{ load.broker_email }}
        </a>
      </p>

      <div class="flex justify-end gap-2 [&>*]:flex-1 md:[&>*]:flex-initial">
        <ButtonComponent @click="handleTrackOffer">
          <router-link
            :to="{
              name: 'Offers',
              hash: `#history-${placed_offer?.id}`,
            }"
          >
            Track Your Offer
          </router-link>
        </ButtonComponent>
        <ButtonComponent
          variant="primary"
          @click="closeModal"
        >
          Done
        </ButtonComponent>
      </div>
    </div>

    <div
      v-else
      class="book-now-inputs -mt-2 flex flex-col gap-3 px-3"
      :class="{
        'max-h-[calc(100vh-100px)]': isMobile,
        'max-h-[80vh]': !isMobile,
      }"
    >
      <div class="-mt-4 text-left">
        <h4 class="text-xl text-steel-1100">Submit Offer</h4>
      </div>

      <div class="flex-between -mt-2">
        <img
          v-if="brokerLogo"
          class="flex h-8 max-w-48 items-center text-2xs"
          :src="brokerLogo"
          alt="broker logo"
        />

        <div class="flex flex-col items-end gap-2 pt-2">
          <h3 class="text-2xl font-semibold">
            {{ formatPrice(load.price, 0) }}
          </h3>
          <p
            v-if="ratePerMile && load?.price"
            class="-mt-2 text-1xs text-neutral-subtlest"
          >
            {{ ratePerMile }}/mi
          </p>
        </div>
      </div>

      <div
        v-if="
          bookItNowType === 'offers_only_static' ||
          bookItNowType === 'offers_and_book_it_now_static'
        "
        class="flex gap-3 rounded-md border border-steel-300 px-4 py-3"
      >
        <img
          class="mt-0.5 h-5 w-5"
          src="@/assets/exclamation.svg"
          alt="exclamation"
        />

        <div>
          <p>Heads up!</p>
          <p class="text-sm font-normal text-steel-800">
            This broker's system cannot support in-app negotiation. You must
            call or email the broker immediately after submitting your offer to
            follow up.
          </p>

          <a
            href="https://service.highway.com/en/articles/13392157-how-to-manage-your-offers#h_15d4a7c891"
            target="_blank"
            class="cursor-pointer text-sm text-sun-700 underline"
          >
            Learn More
          </a>
        </div>
      </div>

      <div
        class="mb-4 flex flex-col gap-4 rounded-xl px-2 py-2"
        :class="{
          '!bg-steel-200': use_posted_offer,
          'border-steel-300': !use_posted_offer,
          '!border-pumpkin-400': rate_error_text,
          '!border-pumpkin-400': confirm_less_than_offer,
          'border-2 border-steel-500': isInputFocused,
          border: !isInputFocused,
        }"
      >
        <div v-if="!use_posted_offer">
          <div class="flex justify-between">
            <span
              v-if="supportsBoth"
              class="cursor-pointer text-left text-xs text-steel-800"
            >
              Name your Offer
            </span>
          </div>
          <div class="rate-input relative">
            <p
              class="absolute -left-2 -top-2 z-20 mt-1 pl-2 text-left text-[32px] font-medium text-steel-900"
            >
              $
            </p>
            <InputComponent
              input-label="rate"
              wrapper-class="flex bg-white text-lg items-center h-10 gap-1.5 pr-2 pl-[28px] outline-none"
              placeholder=""
              :default-input-value="form.rate"
              :value="form.rate"
              @input-change="handleInput"
              @on-blur="handleRateBlur"
            />
            <p
              class="absolute right-8 top-1.5 mt-1 pl-2 text-left font-light text-steel-900"
            >
              {{ inputRatePerMile }}/mi
            </p>
          </div>

          <div
            v-if="rate_error_text"
            class="flex items-center"
          >
            <img
              class="h-4 w-4"
              src="@/assets/lock.svg"
              alt="lock"
            />
            <p
              class="mt-1 pl-2 text-left !text-xs font-normal text-pumpkin-800"
            >
              {{ rate_error_text }}
            </p>
          </div>
        </div>

        <div
          v-else-if="supportsBoth"
          class="flex flex-col justify-between gap-0.5"
        >
          <span class="cursor-pointer text-left text-xs text-steel-800">
            Name your Offer
          </span>

          <div
            class="flex cursor-pointer items-end justify-between gap-2"
            @click="handleNameMyOffer"
          >
            <p class="-mb-1 text-[32px] font-semibold">
              $

              <span class="-ml-1 text-steel-700">
                {{ form.rate }}
              </span>
            </p>
            <p class="font-normal text-steel-700">{{ inputRatePerMile }}/mi</p>
          </div>
        </div>
      </div>

      <div
        v-if="confirm_less_than_offer"
        class="-mt-2 mb-1"
      >
        <p class="text-left text-sm text-pumpkin-800">
          This offer is below the broker's suggested amount. Please confirm that
          this rate is intentional.
        </p>
      </div>

      <div class="mb-4 flex gap-2">
        <ButtonComponent
          v-if="confirm_less_than_offer"
          class="flex !h-12 w-1/2 min-w-[150px] flex-1 border border-steel-300 md:w-min"
          @click="handleCancelConfirmLessThanOffer"
        >
          Cancel
        </ButtonComponent>
        <ButtonComponent
          v-if="isEditingOffer"
          class="flex !h-12 w-1/2 min-w-[150px] flex-1 border border-steel-300 !text-red-500 md:w-min"
          @click="$emit('withdraw')"
        >
          Withdraw Offer
        </ButtonComponent>
        <ButtonComponent
          variant="primary"
          :class="
            isEditingOffer
              ? 'flex !h-12 w-1/2 !min-w-[150px] flex-1 md:w-min'
              : 'flex !h-12 w-full !min-w-[150px] flex-1'
          "
          :is-loading="isLoading"
          :disabled="isSubmitButtonDisabled"
          @click="handleBookNow"
        >
          {{ buttonText }}
        </ButtonComponent>
      </div>
    </div>
  </bottom-sheet>
</template>

<script setup>
import {
  ref,
  computed,
  onBeforeMount,
  watch,
  onMounted,
  onUnmounted,
} from 'vue';
import { useStore } from 'vuex';
import debounce from 'lodash.debounce';
import dayjs from 'dayjs';
import { BottomSheet, InputComponent, ButtonComponent } from '@/components';
import { BookLoadNotification } from '@/components/loads';
import { useScreenSize } from '@/composables';
import {
  formatPrice,
  formatPriceWithoutMoneySign,
  useClickAway,
} from '@/utils';
import { formatPhoneParenthesis } from '@/common';

const store = useStore();

const props = defineProps({
  visible: Boolean,
  loadId: [String, Number],
});

const emit = defineEmits(['close', 'withdraw']);

const { screenSize } = useScreenSize();

const isMobile = computed(() => screenSize.value === 'mobile');

const form = ref({
  rate: '',
  ratecon_email: '',
  ratecon_email_id: '',
  dispatch_name: '',
  dispatch_phone: '',
  dispatch_email: '',
  driver_name: '',
  driver_phone: '',
  vehicle: {},
  vehicle_query: '',
});

const currentUser = computed(() => store.state.users?.currentUser);
const load = computed(() => store.state.loads.load_details);

const rateConEmail = ref('');

const bookItNowType = computed(() => load?.value?.book_it_now_type);
const isStaticType = computed(
  () =>
    bookItNowType.value === 'offers_only_static' ||
    bookItNowType.value === 'offers_and_book_it_now_static'
);
const showStaticModal = ref(false);

const supportsBoth = computed(() => {
  const hasType =
    bookItNowType.value === 'offers_and_book_it_now' ||
    bookItNowType.value === 'offers_and_book_it_now_static';
  const hasPrice = !!load?.value?.price;

  return hasType && hasPrice;
});

const placed_offer = computed(() => store.state.loads?.offer);
const isEditingOffer = computed(
  () =>
    placed_offer?.value?.id &&
    placed_offer?.value?.status !== 'rejected' &&
    placed_offer?.value?.status !== 'expired'
);

const use_posted_offer = ref(false);

const handleUsePostedOffer = () => {
  use_posted_offer.value = !use_posted_offer.value;

  form.value.rate = formatPriceWithoutMoneySign(load?.value?.price, 0);
};

const handleNameMyOffer = () => {
  handleUsePostedOffer();

  form.value.rate = '';

  setTimeout(() => {
    document.querySelector('input[name="rate"]')?.focus();
  }, 0);
};

// check if company paywall can use Secure Rate Confirmation Delivery
const canSRCD = computed(() => load?.value?.can_rate_confirmation);

const carrier_vehicles = computed(
  () => store.state.loads?.eld_carrier_vehicles
);

const driver_same_as_dispatch = ref(true);
const initialRate = ref('');

onBeforeMount(async () => {
  const carrier_id =
    currentUser?.value?.requested_carrier_id ||
    currentUser?.value?.company?.carrier_id;

  const [carrierDetailRes, emailRes] = await Promise.all([
    store.dispatch('carriers/getCarriersDetail', {
      id: carrier_id,
    }),
    store.dispatch('emails/getEmails', {
      query: `?q[carrier_id_eq]=${carrier_id}&q[is_active_eq]=1&q[s]=value+asc`,
    }),
    store.dispatch('contacts/getContactsByCarrierId', {
      carrierId: carrier_id,
    }),
    store.dispatch('loads/getCarrierVehicles', {
      id: carrier_id,
      query: `&q[without_active_truck_postings]=1&q[is_eld_active_eq]=1&q[s]=truck_number+asc`,
    }),
  ]);

  const dispatchConactInfo =
    carrierDetailRes?.data?.contact_information?.dispatch_contact;

  const findCurrentUserEmail = emailRes?.find(
    (el) =>
      el.value === store.state.users?.currentUser?.email &&
      el?.is_type === 'rate_confirmation'
  );

  const findEmail = emailRes?.find((el) => el?.is_type === 'rate_confirmation');

  const initialEmail = findCurrentUserEmail || findEmail || emailRes[0];

  const carrierRateConEmail = `dot${carrierDetailRes?.data?.dot_number}@highway.com`;
  rateConEmail.value = carrierRateConEmail;

  const email = canSRCD?.value
    ? carrierRateConEmail
    : store.state.users?.currentUser?.email;

  const formattedRate = formatPriceWithoutMoneySign(load?.value?.price, 0);
  form.value.rate = formattedRate;
  initialRate.value = formattedRate;
  form.value.ratecon_email = email;
  form.value.ratecon_email_id = initialEmail?.id;
  form.value.dispatch_name = dispatchConactInfo?.name;
  form.value.dispatch_phone = dispatchConactInfo?.phone;
  form.value.dispatch_email = dispatchConactInfo?.email_address;

  if (
    load?.value?.book_it_now_type === 'book_it_now_only' ||
    load?.value?.book_it_now_type === 'book_it_now_only_static'
  ) {
    if (load?.value?.price) {
      use_posted_offer.value = true;
    } else {
      use_posted_offer.value = false;
    }
  } else if (
    load?.value?.book_it_now_type === 'offers_and_book_it_now' ||
    load?.value?.book_it_now_type === 'offers_and_book_it_now_static'
  ) {
    if (!load?.value?.price) {
      use_posted_offer.value = false;
    } else {
      use_posted_offer.value = true;
    }
  }

  if (isEditingOffer?.value) {
    const findOfferVehicle = carrier_vehicles.value?.find(
      (vehicle) => vehicle?.id === placed_offer.value?.carrier_vehicle_id
    );

    if (findOfferVehicle) {
      form.value.vehicle = findOfferVehicle;
    }

    form.value.rate = formatPriceWithoutMoneySign(placed_offer?.value?.rate, 0);

    if (placed_offer.value?.driver_name !== placed_offer.value?.dispatch_name) {
      driver_same_as_dispatch.value = false;

      form.value.driver_name = placed_offer.value?.driver_name;
      form.value.driver_phone = placed_offer.value?.driver_phone;
    }
  }
});

const brokerLogo = computed(() => load?.value?.broker_logo?.url);
const ratePerMile = computed(() => {
  return formatPrice(load?.value?.rate_per_mile);
});

const inputRatePerMile = computed(() => {
  if (
    form.value.rate &&
    parseInt(form.value.rate?.replace(',', '')) === parseInt(load.value?.price)
  ) {
    return formatPrice(load?.value?.rate_per_mile);
  } else {
    const updatedRate =
      parseInt(form.value?.rate?.replace(',', '')) /
      parseInt(load.value?.posted_miles);
    return formatPrice(updatedRate);
  }
});

const showVehicleDropdown = ref(false);

const vehicleSearchRef = ref();
useClickAway(vehicleSearchRef, () => {
  showVehicleDropdown.value = false;
});

const showRateConDropdown = ref(false);

const emailsRef = ref(null);
useClickAway(emailsRef, () => {
  showRateConDropdown.value = false;
});

const showContactsDropdown = ref(false);

const dispatchRef = ref(null);
useClickAway(dispatchRef, () => {
  showContactsDropdown.value = false;
});

watch(driver_same_as_dispatch, (newValue) => {
  if (newValue) {
    form.value.driver_name = form.value.dispatch_name;
    form.value.driver_phone = form.value.dispatch_phone;
  }
});

watch(use_posted_offer, (newValue) => {
  if (!newValue) {
    form.value.vehicle = {};
  }
});

watch(
  () => form.value.rate,
  (newValue) => {
    if (newValue === '') {
      rate_error_text.value = '';
      confirm_less_than_offer.value = false;
      is_less_than_offer.value = false;
    }
  },
  { deep: true }
);

const isInputFocused = ref(false);

onMounted(() => {
  const handleFocusIn = (e) => {
    if (e.target && e.target.tagName === 'INPUT') {
      isInputFocused.value = true;
    }
  };
  const handleFocusOut = () => {
    // Check if the new active element is not an input
    if (
      !(document.activeElement && document.activeElement.tagName === 'INPUT')
    ) {
      isInputFocused.value = false;
    }
  };
  window.addEventListener('focusin', handleFocusIn);
  window.addEventListener('focusout', handleFocusOut);

  // Clean up event listeners
  onUnmounted(() => {
    window.removeEventListener('focusin', handleFocusIn);
    window.removeEventListener('focusout', handleFocusOut);
  });
});

const isLoading = computed(() => store.state.loads?.is_book_now_loading);

const rate_error = ref(false);
const rate_error_text = ref('');

const buttonText = computed(() => {
  if (confirm_less_than_offer.value) return 'Confirm Offer';
  if (rate_error_text.value) return 'Increase Offer to Post';
  if (isEditingOffer?.value) return 'Edit Offer';
  if (use_posted_offer?.value) return 'Book Now';
  return 'Place Offer';
});

const handleRateBlur = () => {
  if (rate_error.value) {
    rate_error_text.value = 'Your offer must be greater than or equal to $1.';
  } else {
    rate_error_text.value = '';
  }
};

const is_less_than_offer = ref(false);
const confirm_less_than_offer = ref(false);

const debouncedRate = debounce((val) => {
  // raw digits only
  const digits = val.replace(/[^\d]/g, '');

  if (!digits) {
    form.value.rate = '';
    rate_error_text.value = '';
    return;
  }

  // convert to number, preserving decimals
  const num = parseFloat(val.replace(/[^0-9.]/g, ''));

  if (load.value.price && bookItNowType.value !== 'offers_only') {
    const isRateGreaterThanLoadPrice = num >= +load.value?.price;

    if (num < 1) {
      rate_error.value = true;
    } else if (!isRateGreaterThanLoadPrice) {
      is_less_than_offer.value = true;
      rate_error.value = false;
    } else {
      is_less_than_offer.value = false;
      confirm_less_than_offer.value = false;
      rate_error_text.value = '';
    }
  } else {
    if (num >= 1) {
      rate_error.value = false;
    } else {
      rate_error.value = true;
    }
  }

  // format with commas (no decimals here, but you could add them if you want)
  const formattedRate = num.toLocaleString('en-US');
  form.value.rate = formattedRate;

  if (
    num === +load.value.price &&
    (bookItNowType.value === 'offers_and_book_it_now' ||
      bookItNowType.value === 'offers_and_book_it_now_static')
  ) {
    use_posted_offer.value = true;
  }

  handleRateBlur();
}, 200);

const handleInput = (value, name) => {
  if (name === 'rate') {
    debouncedRate(value);
  } else {
    form.value[name] = value;
  }
};

const isSubmitButtonDisabled = computed(() => {
  if (!use_posted_offer.value) {
    return rate_error.value || !form.value.rate || form.value.rate === '-';
  } else {
    return rate_error.value || !form.value.rate || form.value.rate === '-';
  }
});

const handleCancelConfirmLessThanOffer = () => {
  confirm_less_than_offer.value = false;
  is_less_than_offer.value = false;

  form.value.rate = formatPriceWithoutMoneySign(load?.value?.price, 0);
};

const handleBookNow = async () => {
  if (is_less_than_offer.value) {
    confirm_less_than_offer.value = true;
    is_less_than_offer.value = false;
    return;
  }

  console.log('Handle book now selection', form?.value);

  const expiresAtTime = dayjs().add(24, 'hour').format('YYYY-MM-DD HH:mm:ss');

  let title;
  let subText;
  let res;
  if (isEditingOffer.value) {
    res = await store.dispatch('loads/editOffer', {
      id: placed_offer?.value?.id,
      params: {
        rate: parseInt(form.value.rate?.replace(',', '')),
        load_id: props?.loadId,
        expires_at: expiresAtTime,
      },
    });

    title = 'Offer Updated';
    subText = "You've successfully updated this offer.";
  } else if (use_posted_offer?.value) {
    // book now request
    res = await store.dispatch('loads/bookNow', {
      id: props?.loadId,
      params: {
        id: props?.loadId,
      },
    });

    title = 'Load Booked';
    subText = "You've successfully booked this load.";
  } else {
    // place offer request
    res = await store.dispatch('loads/placeOffer', {
      id: props?.loadId,
      params: {
        rate: parseInt(form.value.rate?.replace(',', '')),
        load_id: props?.loadId,
        expires_at: expiresAtTime,
      },
    });

    title = 'Offer Placed';
    subText = "You've successfully placed an offer on this load.";
  }

  if ([200, 201].includes(res.status)) {
    await store.dispatch('toast/addToast', {
      component: BookLoadNotification,
      props: {
        visible: true,
        type: 'success',
        title,
        subText,
      },
      duration: 10000,
    });

    use_posted_offer.value = true;

    if (isStaticType.value) {
      showStaticModal.value = true;
    } else {
      emit('close');
    }
  } else {
    await store.dispatch('toast/addToast', {
      component: BookLoadNotification,
      props: {
        visible: true,
        type: 'error',
      },
      duration: 10000,
    });
    emit('close');

    setTimeout(() => {
      store.commit('loads/setBookNowError', {});
    }, 10000);
  }

  confirm_less_than_offer.value = false;
  is_less_than_offer.value = false;
};

const closeModal = () => {
  showStaticModal.value = false;
  if (placed_offer?.value?.id) {
    form.value.rate = String(placed_offer.value?.rate);
  } else {
    form.value.rate = initialRate.value;
  }

  if (
    bookItNowType.value === 'offers_and_book_it_now' ||
    bookItNowType.value === 'offers_and_book_it_now_static'
  ) {
    use_posted_offer.value = true;
  }
  emit('close');
};

const handleTrackOffer = () => {
  localStorage.setItem('current_offer', JSON.stringify(placed_offer?.value));
};
</script>

<style>
.booknow-checkbox .container {
  @apply w-fit pl-9;
}
.booknow-checkbox .container > div {
  @apply font-normal text-steel-900;
}
.booknow-checkbox .container .checkbox-checkmark {
  @apply h-5 w-5;
}
.booknow-checkbox .container .checkbox-checkmark:after {
  border-color: black;
  width: 6px;
  height: 10px;
  border-width: 0 2px 2px 0;
  top: 4px !important;
  left: 7px;
}

.booknow-checkbox .container input:checked ~ .checkbox-checkmark {
  border-color: transparent;
}

.book-now-inputs input {
  @apply text-sm;
}

.rate-input input {
  @apply max-w-full !text-[32px] !font-bold;
}

.rate-input div {
  @apply !outline-none;
}
</style>
